import { Box, chakra, Image, ImageUploadOutline, rem } from '@bonusly/design-system';

import { CelebrationType } from '../../schemas/celebrations';
import WorkAnniversaryBackground from './assets/anniversary-background.jpeg';
import WorkAnniversaryIcon from './assets/anniversary-icon-v2.jpeg';
import BirthdayBackground from './assets/birthday-background.jpeg';
import BirthdayIcon from './assets/birthday-icon-v2.jpeg';
import { ReactComponent as BirthdayImage } from './assets/birthday-post.svg';
import { ReactComponent as CelebrationIcon } from './assets/celebration-icon.svg';
import { ReactComponent as NewHireImage } from './assets/new-hire-post.svg';
import WelcomeBackground from './assets/welcome-background.jpeg';
import WelcomeIcon from './assets/welcome-icon-v2.jpeg';
import { ReactComponent as WelcomeImage } from './assets/welcome-post.svg';
import { ReactComponent as WorkAnniversaryImage } from './assets/work-anniversary-post.svg';

const DefaultIcon = chakra(CelebrationIcon);
const ChakraBirthdayImage = chakra(BirthdayImage);
const ChakraWorkAnniversaryImage = chakra(WorkAnniversaryImage);
const ChakraNewHireImage = chakra(NewHireImage);
const ChakraWelcomeImage = chakra(WelcomeImage);
const ChakraImageUploadOutline = chakra(ImageUploadOutline);

// Redesigned icons
const ChakraBirthdayIcon: typeof Image = (props) => <Image src={BirthdayIcon} {...props} />;
const ChakraWorkAnniversaryIcon: typeof Image = (props) => <Image src={WorkAnniversaryIcon} {...props} />;
const ChakraWelcomeIcon: typeof Image = (props) => <Image src={WelcomeIcon} {...props} />;
const ChakraNewHireIcon: typeof Image = (props) => <Image src={WelcomeIcon} {...props} />;
const ChakraDefaultIcon: typeof Image = (props) => <Image src={WelcomeIcon} {...props} />;
// Redesigned images
const BirthdayBackgroundImage: typeof Image = (props) => <Image src={BirthdayBackground} objectFit="cover" {...props} />;
const WorkAnniversaryBackgroundImage: typeof Image = (props) => <Image src={WorkAnniversaryBackground} objectFit="cover" {...props} />;
const NewHireBackgroundImage: typeof Image = (props) => <Image src={WelcomeBackground} objectFit="cover" {...props} />;
const WelcomeBackgroundImage: typeof Image = (props) => <Image src={WelcomeBackground} objectFit="cover" {...props} />;
const DefaultBackground: typeof Box = (props) => <Box bgColor={'bg.canvas'} {...props} />;

export const getCelebrationStyles = (type: CelebrationType, isSpotlightTriggerEnabled: boolean) => {
	if (isSpotlightTriggerEnabled) {
		switch (type) {
			case CelebrationType.Birthday:
				return {
					bgColor: 'bg.surface',
					iconComponent: <ChakraBirthdayIcon w={10} h={10} />,
					imageComponent: <BirthdayBackgroundImage w="full" h="full" />,
				};

			case CelebrationType.WorkAnniversary:
				return {
					bgColor: 'bg.surface',
					iconComponent: <ChakraWorkAnniversaryIcon w={10} h={10} />,
					imageComponent: <WorkAnniversaryBackgroundImage w="full" h="full" />,
				};

			case CelebrationType.NewHire:
				return {
					bgColor: 'bg.surface',
					iconComponent: <ChakraNewHireIcon w={10} h={10} />,
					imageComponent: <NewHireBackgroundImage w="full" h="full" />,
				};

			case CelebrationType.Welcome:
				return {
					bgColor: 'bg.surface',
					iconComponent: <ChakraWelcomeIcon w={10} h={10} />,
					imageComponent: <WelcomeBackgroundImage w="full" h="full" />,
				};

			default:
				return {
					bgColor: 'bg.canvas',
					iconComponent: <ChakraDefaultIcon w={10} h={10} />,
					imageComponent: <DefaultBackground w="full" h="full" />,
				};
		}
	}

	switch (type) {
		case CelebrationType.Custom:
			return {
				bgColor: 'bg.surface',
				iconComponent: <Box bgColor="secondary.bgAlpha" w={10} h={10} />,
				imageComponent: <Box bgColor="secondary.bgAlpha" w={rem(284)} h={rem(284)} />,
			};

		case CelebrationType.Birthday:
			return {
				bgColor: 'bg.surface',
				iconComponent: <DefaultIcon w={10} h={10} />,
				imageComponent: <BirthdayImage />,
			};

		case CelebrationType.WorkAnniversary:
			return {
				bgColor: 'bg.surface',
				iconComponent: <DefaultIcon w={10} h={10} />,
				imageComponent: <WorkAnniversaryImage />,
			};

		case CelebrationType.NewHire:
			return {
				bgColor: 'bg.surface',
				iconComponent: <DefaultIcon w={10} h={10} />,
				imageComponent: <NewHireImage />,
			};

		case CelebrationType.Welcome:
			return {
				bgColor: 'bg.surface',
				iconComponent: <DefaultIcon w={10} h={10} />,
				imageComponent: <WelcomeImage />,
			};

		default:
			return {
				bgColor: 'bg.surface',
				iconComponent: null,
				imageComponent: null,
			};
	}
};

export const getDefaultIcon = (type: CelebrationType, isSpotlightTriggerEnabled: boolean) => {
	if (isSpotlightTriggerEnabled) {
		switch (type) {
			case CelebrationType.Birthday:
				return ChakraBirthdayIcon;
			case CelebrationType.WorkAnniversary:
				return ChakraWorkAnniversaryIcon;
			case CelebrationType.NewHire:
				return ChakraNewHireIcon;
			case CelebrationType.Welcome:
				return ChakraWelcomeIcon;
			default:
				return ChakraImageUploadOutline;
		}
	}

	if (type === CelebrationType.Custom) {
		return ChakraImageUploadOutline;
	}

	return DefaultIcon;
};

export const getDefaultImage = (type: CelebrationType, isSpotlightTriggerEnabled: boolean) => {
	if (isSpotlightTriggerEnabled) {
		switch (type) {
			case CelebrationType.Birthday:
				return BirthdayBackgroundImage;
			case CelebrationType.WorkAnniversary:
				return WorkAnniversaryBackgroundImage;
			case CelebrationType.NewHire:
				return NewHireBackgroundImage;
			case CelebrationType.Welcome:
				return WelcomeBackgroundImage;
			default:
				return ChakraImageUploadOutline;
		}
	}

	switch (type) {
		case CelebrationType.Birthday:
			return ChakraBirthdayImage;
		case CelebrationType.WorkAnniversary:
			return ChakraWorkAnniversaryImage;
		case CelebrationType.NewHire:
			return ChakraNewHireImage;
		case CelebrationType.Welcome:
			return ChakraWelcomeImage;
		default:
			return ChakraImageUploadOutline;
	}
};
